<template>
  <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
    <div class="flex items-center justify-between">
      <div class="flex-1">
        <div class="flex items-center">
          <div v-if="icon" class="flex-shrink-0 mr-3">
            <div
              class="w-10 h-10 rounded-full flex items-center justify-center relative"
              :class="iconBackgroundClass"
            >
              <i class="material-icons text-white">{{ icon }}</i>
              <!-- Status indicator dot -->
              <div
                v-if="status"
                class="absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white"
                :class="statusDotClass"
              ></div>
            </div>
          </div>
          <div class="flex-1">
            <p
              class="text-sm font-medium text-gray-600 uppercase tracking-wide"
            >
              {{ label }}
            </p>
            <p class="text-2xl font-bold text-gray-900">
              {{ formattedValue }}
            </p>

            <!-- Additional info section -->
            <div v-if="additionalInfo" class="mt-1">
              <p class="text-sm text-gray-700 font-medium">
                {{ additionalInfo.subtitle }}
              </p>
              <p class="text-sm text-gray-600 mt-0.5">
                {{ additionalInfo.description }}
              </p>
            </div>
          </div>
        </div>

        <!-- Progress bar or trend indicator -->
        <div class="mt-3">
          <!-- Progress bar when percentage is provided -->
          <div
            v-if="percentage !== null && percentage !== undefined"
            class="mb-2"
          >
            <div
              class="flex items-center justify-between text-xs text-gray-600 mb-1"
            >
              <span>{{ getProgressLabel() }}</span>
              <span>{{ percentage }}%</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div
                class="h-2 rounded-full transition-all duration-500"
                :class="progressBarClass"
                :style="{ width: percentage + '%' }"
              ></div>
            </div>
          </div>

          <!-- Traditional trend indicator -->
          <div v-else-if="trend" class="flex items-center">
            <div
              class="flex items-center text-sm font-medium"
              :class="trendClass"
            >
              <i class="material-icons text-xs mr-1">
                {{ trend.direction === "up" ? "trending_up" : "trending_down" }}
              </i>
              {{ trend.percentage }}%
            </div>
            <span class="text-sm text-gray-500 ml-2">{{ trend.period }}</span>
          </div>

          <!-- Status indicator when no trend or percentage -->
          <div v-else-if="status" class="flex items-center">
            <div
              class="flex items-center text-sm font-medium"
              :class="statusTextClass"
            >
              <i class="material-icons text-xs mr-1">{{ statusIcon }}</i>
              {{ statusText }}
            </div>
          </div>
        </div>
      </div>

      <!-- Mini chart or health score -->
      <div v-if="showMiniChart || additionalInfo" class="flex-shrink-0 ml-4">
        <div
          v-if="additionalInfo && additionalInfo.healthScore !== undefined"
          class="text-center"
        >
          <div class="w-12 h-12 relative">
            <!-- Circular progress -->
            <svg class="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
              <circle
                cx="18"
                cy="18"
                r="16"
                fill="none"
                class="stroke-current text-gray-200"
                stroke-width="3"
              />
              <circle
                cx="18"
                cy="18"
                r="16"
                fill="none"
                :class="healthScoreColorClass"
                stroke-width="3"
                stroke-linecap="round"
                :stroke-dasharray="`${additionalInfo.healthScore} 100`"
              />
            </svg>
            <div class="absolute inset-0 flex items-center justify-center">
              <span class="text-xs font-bold text-gray-700">
                {{ Math.round(additionalInfo.healthScore) }}
              </span>
            </div>
          </div>
          <p class="text-xs text-gray-500 mt-1">Health</p>
        </div>
        <div
          v-else-if="showMiniChart"
          class="w-16 h-10 bg-gray-100 rounded"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MetricCard",
  props: {
    label: {
      type: String,
      required: true
    },
    value: {
      type: [Number, String],
      required: true
    },
    icon: {
      type: String,
      default: null
    },
    iconColor: {
      type: String,
      default: "blue",
      validator: value =>
        [
          "blue",
          "green",
          "red",
          "yellow",
          "purple",
          "indigo",
          "orange"
        ].includes(value)
    },
    trend: {
      type: Object,
      default: null,
      validator: value => {
        return (
          value === null ||
          (value.hasOwnProperty("direction") &&
            value.hasOwnProperty("percentage") &&
            ["up", "down"].includes(value.direction))
        );
      }
    },
    formatType: {
      type: String,
      default: "number",
      validator: value => ["number", "currency", "percentage"].includes(value)
    },
    showMiniChart: {
      type: Boolean,
      default: false
    },
    additionalInfo: {
      type: Object,
      default: null
    },
    percentage: {
      type: Number,
      default: null
    },
    status: {
      type: String,
      default: null,
      validator: value =>
        value === null ||
        ["excellent", "good", "warning", "critical", "info"].includes(value)
    }
  },
  computed: {
    formattedValue() {
      if (this.formatType === "currency") {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD"
        }).format(this.value);
      } else if (this.formatType === "percentage") {
        return `${this.value}%`;
      } else {
        return new Intl.NumberFormat("en-US").format(this.value);
      }
    },
    iconBackgroundClass() {
      const colorMap = {
        blue: "bg-blue-500",
        green: "bg-green-500",
        red: "bg-red-500",
        yellow: "bg-yellow-500",
        purple: "bg-purple-500",
        indigo: "bg-indigo-500",
        orange: "bg-orange-500"
      };
      return colorMap[this.iconColor] || "bg-blue-500";
    },
    trendClass() {
      if (!this.trend) return "";
      return this.trend.direction === "up" ? "text-green-600" : "text-red-600";
    },
    statusDotClass() {
      const statusColors = {
        excellent: "bg-green-500",
        good: "bg-blue-500",
        warning: "bg-yellow-500",
        critical: "bg-red-500",
        info: "bg-blue-500"
      };
      return statusColors[this.status] || "bg-gray-500";
    },
    statusTextClass() {
      const statusColors = {
        excellent: "text-green-600",
        good: "text-blue-600",
        warning: "text-yellow-600",
        critical: "text-red-600",
        info: "text-blue-600"
      };
      return statusColors[this.status] || "text-gray-600";
    },
    statusIcon() {
      const statusIcons = {
        excellent: "check_circle",
        good: "thumb_up",
        warning: "warning",
        critical: "error",
        info: "info"
      };
      return statusIcons[this.status] || "info";
    },
    statusText() {
      const statusTexts = {
        excellent: "Excellent",
        good: "Good",
        warning: "Attention Needed",
        critical: "Critical",
        info: "Information"
      };
      return statusTexts[this.status] || "Status";
    },
    progressBarClass() {
      if (this.status) {
        const statusProgressColors = {
          excellent: "bg-green-500",
          good: "bg-blue-500",
          warning: "bg-yellow-500",
          critical: "bg-red-500",
          info: "bg-blue-500"
        };
        return statusProgressColors[this.status] || "bg-blue-500";
      }

      // Default color based on percentage
      if (this.percentage >= 70) return "bg-green-500";
      if (this.percentage >= 50) return "bg-blue-500";
      if (this.percentage >= 30) return "bg-yellow-500";
      return "bg-red-500";
    },
    healthScoreColorClass() {
      if (
        !this.additionalInfo ||
        this.additionalInfo.healthScore === undefined
      ) {
        return "stroke-current text-blue-500";
      }

      const score = this.additionalInfo.healthScore;
      if (score >= 80) return "stroke-current text-green-500";
      if (score >= 60) return "stroke-current text-blue-500";
      if (score >= 40) return "stroke-current text-yellow-500";
      return "stroke-current text-red-500";
    }
  },
  methods: {
    getProgressLabel() {
      if (this.label.toLowerCase().includes("active")) {
        return "Activity Rate";
      }
      if (this.label.toLowerCase().includes("expired")) {
        return "Expiration Rate";
      }
      if (this.label.toLowerCase().includes("non-renewing")) {
        return "Non-Renewal Rate";
      }
      return "Percentage";
    }
  }
};
</script>

<style scoped>
/* Smooth animations for progress bars */
.transition-all {
  transition: all 0.3s ease-in-out;
}

/* Circular progress animation */
circle {
  transition: stroke-dasharray 0.5s ease-in-out;
}
</style>
